import React, { createContext, useContext, useState, useEffect } from 'react';
import { login as apiLogin, createUser } from '../services/api';
import { User } from '../types/User';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  signUp: (formData: any) => Promise<void>;
  logout: () => void;
  profileUpdate: (updatedUser: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    // Check for stored user data on initial load
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const userData = JSON.parse(storedUser);
      setUser(userData);
    }
  }, []);

  const login = async (email:string, password: string) => {
    try {
      const response = await apiLogin(email, password);
      
      const {  token, user: userData } = response.data;
      
      // Create a User object with the required email field
      const user: User = {
        id: userData.id,
        username: userData.username,
        firstName: userData.firstName,
        lastName: userData.lastName,
        imageUrl: userData.imageUrl,
        phoneNumber: userData.phoneNumber,
        email: userData.email || `${userData.username}@example.com`, // Fallback if email is not provided
      };
      localStorage.setItem('userToken', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      setUser(user);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const signUp = async (formData: any) => {
    const userPayload = {
      email: formData.email,
      password: formData.password,
      username: formData.username,
      firstName: formData.firstName,
      lastName: formData.lastName,
      role: formData.role,
      confirmPassword: formData.confirmPassword,
      phoneNumber: formData.phoneNumber
    };

    try {
      const response = await createUser(userPayload);
      const {  token, user: userData }: any = response.data;
      // Create a User object with the required email field
      const user: User = {
        id: userData.id,
        username: userData.username,
        firstName: userData.firstName,
        lastName: userData.lastName,
        imageUrl: userData.imageUrl,
        phoneNumber: userData.phoneNumber,
        email: userData.email || `${userData.username}@example.com`, // Fallback if email is not provided
        role: userData.role as 'user'
      };
      localStorage.setItem('userToken', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      setUser(user);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('userToken');
    localStorage.removeItem('user');
  };

  const profileUpdate = (updatedUser: User) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: !!user, signUp, login, logout, profileUpdate }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 